import apiClient from "../../client/apiClient"
import { appendQueryParams } from "../../utils"

export type ContractorStatus = "active" | "inactive"

/** Backend may support type: "school" | "private" for private/personal van contractors */
export type ContractorType = "school" | "private"

export interface Contractor {
  id: string
  key: string
  name: string
  area: string
  cnic: string
  status: ContractorStatus
  type?: ContractorType
  /** School keeps this % of transport fee; contractor gets the rest (set at contractor create time). */
  commissionPercentage?: number
  createdAt?: string
  updatedAt?: string
}

export interface PaginationData {
  currentPage: number
  hasNextPage: boolean
  hasPrevPage: boolean
  limit: number
  total: number
  totalPages: number
}

export interface ContractorsResponse {
  data: any[]
  pagination: PaginationData
}

const CONTRACTOR_ENDPOINT = {
  CONTRACTORS: "/transport/contractors",
}

type ListContractorsParams = {
  page?: number
  limit?: number
  searchText?: string
  status?: string
  area?: string
  sortBy?: string
  sortOrder?: string
  /** "school" | "private" – filter by contractor type (e.g. only school vehicle contractors) */
  contractorType?: ContractorType
  type?: ContractorType
}

export const getAllContractors = async ({
  page = 1,
  limit = 20,
  searchText,
  status,
  area,
  sortBy,
  sortOrder,
  contractorType,
  type,
}: ListContractorsParams = {}): Promise<ContractorsResponse> => {
  const typeFilter = contractorType ?? type
  const response = await apiClient({
    url: appendQueryParams(CONTRACTOR_ENDPOINT.CONTRACTORS, {
      page,
      limit,
      searchText,
      status,
      area,
      sortBy,
      sortOrder,
      ...(typeFilter && { contractorType: typeFilter }),
    }),
    method: "GET",
  })

  return response.data
}

/** Area: send as comma-separated string for API (backend may expect single string) */
type CreateContractorPayload = {
  name: string
  area: string // comma-separated labels from multiselect
  cnic: string
  status: ContractorStatus
  type?: ContractorType // "private" for students' personal vans
  /** School keeps this % of transport fee; contractor gets the rest. Used for payout. */
  commissionPercentage?: number
}

export const createContractor = async (payload: CreateContractorPayload) => {
  const data: Record<string, unknown> = {
    ...payload,
    cnic_number: payload.cnic,
    contractorType: payload.type,
  }
  if (
    payload.commissionPercentage != null &&
    !isNaN(Number(payload.commissionPercentage))
  ) {
    data.commissionPercentage = Math.min(
      100,
      Math.max(0, Number(payload.commissionPercentage)),
    )
  }
  const response = await apiClient({
    url: CONTRACTOR_ENDPOINT.CONTRACTORS,
    method: "POST",
    data,
  })

  return response.data
}

type UpdateContractorPayload = {
  name: string
  area: string // comma-separated labels from multiselect
  status: ContractorStatus
  type?: ContractorType
  commissionPercentage?: number
}

export const updateContractor = async (
  contractorId: string,
  payload: UpdateContractorPayload,
) => {
  const data: Record<string, unknown> = {
    ...payload,
    contractorType: payload.type,
  }
  if (
    payload.commissionPercentage != null &&
    !isNaN(Number(payload.commissionPercentage))
  ) {
    data.commissionPercentage = Math.min(
      100,
      Math.max(0, Number(payload.commissionPercentage)),
    )
  }
  const response = await apiClient({
    url: `${CONTRACTOR_ENDPOINT.CONTRACTORS}/${contractorId}`,
    method: "PUT",
    data,
  })

  return response.data
}
